import React, {createContext, useEffect, useReducer, useCallback, useMemo} from "react";
import PropTypes from "prop-types";
import ResortsReducer from "../reducers/resortsReducer";
import {apartmentSearchAPI} from "../../common/api/apartmentSearchAPI";
import {API_APARTMENT_SEARCH_RESORTS} from "../../common/api/apiConstants";
import {createURLQuery} from "../../common/api/apiUtil";
import {transformResorts} from "../../selectors/resorts";
import {useI18N} from "../../common/common";

const initialState = {
    resorts: [],
    partnerResorts: [],
    nonPartnerResorts: [],
    resortsLoading: false,
    error: null,
    init: undefined
};

const ResortsProvider = ({children}) => {
    const {i18n} = useI18N();
    const [resortsState, resortsDispatch] = useReducer(ResortsReducer, initialState);

    // // We use this useEffect in order to make sure that the api request will run only once upon initialization of the state
    useEffect(() => {
        if (resortsState?.init) {
            apartmentSearchAPI.get(API_APARTMENT_SEARCH_RESORTS + createURLQuery({
                locale: resortsState?.init?.locale,
                is_bookable: resortsState?.init?.is_bookable
            }))
                .then(res => res.data)
                .then(data => {
                    resortsDispatch({type: "GET_RESORTS_SUCCESS", payload: transformResorts(data)});
                })
                .catch(err => {
                    resortsDispatch({type: "GET_RESORTS_ERROR", payload: err});
                    throw err;
                });
        }
    }, [resortsState?.init]);

    const getResortIds = useCallback((resorts, country, destination) => {
        const resortIds = [];
        if (country) {
            if (destination) {
                // Get resort ids of the destination
                resorts.forEach((resort) => {
                    if (resort?.destination?.country?.slug === country && resort?.destination?.slug === destination) {
                        resortIds.push(resort?.hapimagResortId.toString());
                    }
                });
            } else {
                // Get resort ids of the country
                resorts.forEach((resort) => {
                    if (resort?.destination?.country?.slug === country || (country === i18n("view.booking_information.partner_resorts.slug") && resort?.isPartner)) {
                        resortIds.push(resort?.hapimagResortId.toString());
                    }
                });
            }
        }
        return resortIds;
    }, [resortsState]);

    const resortsStateAndIds = useMemo(
        () => {
            const resorts = resortsState?.resorts?.map((resort) => ({...resort, isPartner: resort.resortType === "partner"}));
            return {
                ...resortsState,
                resorts: resorts,
                getResortIds,
                partnerResorts: resorts?.filter((item) => item.isPartner),
                nonPartnerResorts: resorts?.filter((item) => !item.isPartner)
            };
        }, [resortsState, getResortIds]
    );

    return (
        <ResortsContext.Provider value={[resortsStateAndIds, resortsDispatch]}>
            {children}
        </ResortsContext.Provider>
    );
};

ResortsProvider.propTypes = {
    children: PropTypes.any
};

export const ResortsContext = createContext(initialState);
export default ResortsProvider;
